/**
 * License Expiration Domain Logic
 *
 * Pure functions for calculating license expiration dates.
 * Date-only values are calendar dates, parsed at local midnight (never as UTC timestamps).
 */

export const EXPIRATION_TYPE_ONE_YEAR = 'one-year';
export const EXPIRATION_TYPE_END_OF_YEAR = 'end-of-year';
export const EXPIRATION_TYPE_ANNUAL_DATE = 'annual-date';
export const DEFAULT_PERIOD_START_MONTH_DAY = '01-01';

/** Validate an annual period start. February 29 uses February 28 in non-leap years. */
export function normalizePeriodStartMonthDay(value) {
  if (value == null) return DEFAULT_PERIOD_START_MONTH_DAY;
  if (typeof value !== 'string' || !/^\d{2}-\d{2}$/.test(value)) {
    throw new Error('licensePeriodStartMonthDay must be a valid MM-DD calendar date');
  }
  const [month, day] = value.split('-').map(Number);
  const date = new Date(2000, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error('licensePeriodStartMonthDay must be a valid MM-DD calendar date');
  }
  return value;
}

/** Read the period start, including the field used by the first annual-date release. */
export function resolveLicensePeriodStartMonthDay(policy = {}) {
  return (policy.licensePeriodStartMonthDay === undefined
    ? policy.licenseExpirationMonthDay : policy.licensePeriodStartMonthDay) ?? DEFAULT_PERIOD_START_MONTH_DAY;
}

/** Optional, one-time launch date. It must be an actual start of the annual period. */
export function normalizeFirstLicensePeriodStartsOn(value, periodStartMonthDay) {
  if (value == null) return null;
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? normalizeToDate(value) : null;
  if (!date || date.getFullYear() > 9998) {
    throw new Error('First license period starts on must be a valid YYYY-MM-DD calendar date');
  }
  const [month, day] = normalizePeriodStartMonthDay(periodStartMonthDay).split('-').map(Number);
  if (formatDateString(calendarDate(date.getFullYear(), month - 1, day)) !== value) {
    throw new Error('First license period starts on must match the annual period start month and day');
  }
  return value;
}

/** expiresOn is the last valid calendar day; expiration starts the following day. */
export function isLicenseExpired(expiresOn, now = new Date()) {
  const date = normalizeToDate(expiresOn);
  return Boolean(date && !isNaN(date.getTime()) && formatDateString(date) < todayString(now));
}

/**
 * Format a Date as YYYY-MM-DD.
 * @param {Date|null} date
 * @returns {string|null}
 */
export function formatDateString(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Return today's date as YYYY-MM-DD.
 * @param {Date} [now=new Date()]
 * @returns {string}
 */
export function todayString(now = new Date()) {
  return formatDateString(now);
}

/**
 * Compute the license expiration date based on the issued date and expiration policy.
 *
 * @param {Object} params
 * @param {Date|string|number|null|undefined} params.dateIssued - The date the license was issued
 * @param {string} params.expirationType - 'one-year', 'annual-date', or legacy 'end-of-year'
 * @param {string} [params.periodStartMonthDay='01-01'] - Start of the next annual period
 * @param {string|null} [params.firstPeriodStartsOn] - Earliest annual period, including its year
 * @param {number} [params.earlyIssueMonthAllowance=0] - Calendar months before the cutoff
 *   during which an issue rolls over by another cycle. The allowance boundary is inclusive.
 * @returns {Date|null} The expiration date, or null if dateIssued is invalid
 */
export function computeLicenseExpirationDate({
  dateIssued,
  expirationType,
  periodStartMonthDay,
  firstPeriodStartsOn,
  earlyIssueMonthAllowance = 0
}) {
  // Parse and validate the issued date
  const issued = normalizeToDate(dateIssued);
  if (!issued || isNaN(issued.getTime())) {
    return null;
  }

  // Clamp earlyIssueMonthAllowance to valid range [0, 11]
  const allowance = Math.max(0, Math.min(11, earlyIssueMonthAllowance || 0));

  switch (expirationType?.replace(/_/g, '-').toLowerCase()) {
    case EXPIRATION_TYPE_ONE_YEAR:
      return computeOneYearExpiration(issued);

    case EXPIRATION_TYPE_ANNUAL_DATE:
    case EXPIRATION_TYPE_END_OF_YEAR:
    default: {
      const monthDay = normalizePeriodStartMonthDay(periodStartMonthDay);
      const first = normalizeToDate(normalizeFirstLicensePeriodStartsOn(firstPeriodStartsOn, monthDay));
      // Prelaunch issues use the first annual period, preserving the actual issue date.
      return computeAnnualExpiration(first && issued < first ? first : issued, monthDay, allowance);
    }
  }
}

/**
 * Compute expiration date, string, and expired flag.
 *
 * @param {Object} params
 * @param {Date|string|number|null|undefined} params.dateIssued
 * @param {string} params.expirationType
 * @param {string} [params.periodStartMonthDay='01-01']
 * @param {string|null} [params.firstPeriodStartsOn] - Optional first annual period
 * @param {number} [params.earlyIssueMonthAllowance=1]
 * @param {Date} [params.now=new Date()]
 * @returns {{ expirationDate: Date|null, expiresOn: string|null, isExpired: boolean }}
 */
export function computeExpirationFields({
  dateIssued,
  expirationType,
  periodStartMonthDay,
  firstPeriodStartsOn,
  earlyIssueMonthAllowance = 1,
  now = new Date()
}) {
  const expirationDate = computeLicenseExpirationDate({
    dateIssued,
    expirationType,
    periodStartMonthDay,
    firstPeriodStartsOn,
    earlyIssueMonthAllowance
  });
  const expiresOn = formatDateString(expirationDate);
  const isExpired = isLicenseExpired(expiresOn, now);
  return { expirationDate, expiresOn, isExpired };
}

/**
 * Compute the last valid day before the next one-year period begins.
 * A February 29 issue starts its next period on March 1 in a non-leap year,
 * so the current period remains valid through February 28.
 *
 * @param {Date} issued - The issued date (already validated)
 * @returns {Date} The expiration date
 */
function computeOneYearExpiration(issued) {
  // Calendar overflow intentionally maps February 29 to March 1 next year.
  const nextPeriodStart = new Date(issued.getFullYear() + 1, issued.getMonth(), issued.getDate());
  return new Date(nextPeriodStart.getFullYear(), nextPeriodStart.getMonth(), nextPeriodStart.getDate() - 1);
}

/** Construct a calendar date, clamping the day instead of overflowing into the next month. */
function calendarDate(year, month, day) {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
}

function computeAnnualExpiration(issued, monthDay, allowance) {
  const [month, day] = monthDay.split('-').map(Number);
  let year = issued.getFullYear();
  let cutoff = calendarDate(year, month - 1, day);
  if (issued >= cutoff) {
    cutoff = calendarDate(++year, month - 1, day);
  }

  // Subtract from the actual cutoff so windows can cross January 1 or end mid-month.
  const windowMonth = new Date(cutoff.getFullYear(), cutoff.getMonth() - allowance, 1);
  const earlyWindow = calendarDate(windowMonth.getFullYear(), windowMonth.getMonth(), cutoff.getDate());
  if (issued >= earlyWindow) {
    cutoff = calendarDate(year + 1, month - 1, day);
  }
  // Calendar arithmetic preserves the last valid day across leap years and DST.
  return new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate() - 1);
}

/**
 * Normalize various date representations to a Date object.
 * @param {Date|string|number|null|undefined} value
 * @returns {Date|null}
 */
function normalizeToDate(value) {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const calendar = trimmed.match(/^(\d{4})-?(\d{2})-?(\d{2})$/);
    if (calendar) {
      const [, year, month, day] = calendar.map(Number);
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
        ? date : null;
    }
    const date = new Date(trimmed);
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  // Handle Firestore Timestamp-like objects
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return value.toDate();
  }
  // Firestore timestamps serialized through JSON no longer have a toDate method.
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const date = new Date(seconds * 1000 + (value.nanoseconds ?? value._nanoseconds ?? 0) / 1e6);
      return isNaN(date.getTime()) ? null : date;
    }
  }
  return null;
}

/**
 * Backward-compatible alias for computeLicenseExpirationDate.
 * Matches the signature from the original LicenseExpirationType.js.
 *
 * @param {Date|moment|Object|string|number|null} dateIssued - The date the license was issued
 * @param {string} expirationType - 'one-year', 'annual-date', or 'end-of-year'
 * @param {number} [earlyIssueMonthAllowance=1] - Months before year-end that roll over
 * @param {string} [periodStartMonthDay='01-01'] - Annual cutoff
 * @param {string|null} [firstPeriodStartsOn] - Optional first annual period
 * @returns {Date|null} The expiration date, or null if dateIssued is invalid
 */
export function calculateExpirationDate(dateIssued, expirationType, earlyIssueMonthAllowance = 1, periodStartMonthDay, firstPeriodStartsOn) {
  return computeLicenseExpirationDate({
    dateIssued,
    expirationType,
    periodStartMonthDay,
    firstPeriodStartsOn,
    earlyIssueMonthAllowance
  });
}
